/**
 * AI Assistant State
 */

import { AICommandPlan, ConversationSummary, StepExecutionResult } from '@infrastructure/aiAssistant/types';
import type { MessageBlock } from './messageBlocks';
import type { CandidateGraph } from './rulePreview';
import type { GeneratedReportBody, GenerationFailure } from '@components/application/Reports/GenerateReport/generateReport';

/**
 * Report-preview artifact carried on an assistant message when the
 * `generate_report` capability tool returns (TV1-2059 · S2). It is the chat
 * counterpart of the Reports page's inline preview, and models the tool's two
 * terminal shapes 1:1 (see `capabilities/read/generate_report.ts`):
 *
 *  - `success` -> the unpersisted, positionless layout preview the Reports page
 *    already renders (`toPreviewLayout` + `WidgetLayoutComponent`). Saving it is
 *    the same pure `AddUiWidgetLayout` pass-through the manual builder uses.
 *  - `failure` -> the structured 422 the S1 backend tool already classified
 *    (`reason` + optional `explanation` / `missingConcepts` / `nearestReport`),
 *    rendered exactly like `GenerateReportBar`'s failure alert.
 */
export type ReportPreview =
  | { status: 'success'; body: GeneratedReportBody }
  | { status: 'failure'; failure: GenerationFailure };

export interface AIChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  timestamp: number;
  isLoading?: boolean;
  commandPlan?: AICommandPlan;
  executionResults?: StepExecutionResult[];
  planStatus?: 'pending' | 'executing' | 'completed' | 'failed' | 'aborted' | 'discarded';
  /** Set when a `generate_report` tool-result streams in for this turn. */
  reportPreview?: ReportPreview;
  /**
   * Structured UI attached to this message — a channel picker, an import
   * progress card. Rendered through `messageBlocks`' registry, so a new block
   * type costs a renderer registration rather than a new field here plus a new
   * branch in the message component (which is what `commandPlan` and
   * `reportPreview` above each cost).
   *
   * An unknown type renders nothing; the message's own text still shows.
   */
  block?: MessageBlock;
  /**
   * OTel trace of the agent run that produced this answer (TV1-2417), learned from
   * the terminal `done` StreamUpdate. It is the handle a thumbs up/down is scored
   * against in Langfuse — the answer, not the conversation.
   *
   * USUALLY ABSENT, and that is normal, not a bug: the backend emits it only when a
   * recording tracer provider is wired, and no deployed environment configures the
   * Langfuse exporter today. Nothing renders differently without it; the feedback
   * click is simply not forwarded (see `feedbackPolicy`).
   *
   * ⚠️ IN-MEMORY ONLY. Nothing persists it, so a rehydrated conversation
   * (`getMessages`) carries no trace ids and its answers cannot be rated after a
   * reload. Deliberate: the trace is a live-run artifact, and persisting it would
   * mean projecting an observability id onto the conversation readmodel.
   */
  traceId?: string;
  /**
   * The rating the seller gave this answer, held OPTIMISTICALLY.
   *
   * Set the instant she clicks, before (and regardless of) the POST. A score is
   * telemetry: making the button wait on a network round-trip — or worse, snap back
   * when a sink that is not configured anywhere declines it — would punish the act of
   * giving feedback, and NEGATIVE feedback is the half worth having.
   */
  feedback?: 'up' | 'down';
}

/**
 * Pending approval state for an `external-irreversible` capability tool call.
 *
 * When an `interrupt` StreamUpdate arrives carrying tool name + args, this slice
 * holds the data so the approval UI can render. The user's Approve / Reject
 * decision is dispatched as a separate command and consumed by the approval
 * policy, which calls `ResumeAgentRun` against the backend.
 */
export interface PendingApproval {
  toolName: string;
  humanizedName: string;
  args: Record<string, unknown>;
  conversationId: string;
}

export type ApprovalDecision = 'approved' | 'rejected';

/**
 * How the assistant panel is presented.
 *
 *  - `floating` — the historical draggable window (`FloatingPanel`), ~25vw wide,
 *    anchored under the navbar. Unchanged behaviour.
 *  - `fullscreen` — the panel fills the viewport **below the navbar**, which stays
 *    visible and interactive. Deliberately not a true full-viewport overlay: the
 *    user must never lose the app chrome, and the panel's z-index sits under it.
 *
 * This is presentation-only: the conversation, its history and every in-flight
 * plan are untouched by a mode switch.
 */
export type AIAssistantPanelMode = 'floating' | 'fullscreen';

export interface AIAssistantState {
  isPanelOpen: boolean;
  panelMode: AIAssistantPanelMode;
  conversationId: string | null;
  messages: AIChatMessage[];
  isProcessing: boolean;
  processingStep: string;
  autoExecute: boolean;
  executingPlanMessageId: string | null;
  currentExecutingStepIndex: number;
  conversationHistory: ConversationSummary[];
  isLoadingHistory: boolean;
  processingPhase: 'discovery' | 'action' | null;
  stepError: string | null;
  planValidationError: string | null;
  pendingApproval: PendingApproval | null;
  pendingApprovalDecision: ApprovalDecision | null;
  /**
   * The last rule graph `compose_rule` produced this session (TV1-2340 · P5).
   *
   * It exists so the SECOND turn of a refinement ("no, sólo para el canal X")
   * can start from what the assistant just drew instead of recomposing from
   * scratch. `backendPolicy` re-injects it into the outgoing request's
   * `context.storeStates`.
   *
   * ⚠️ IN-MEMORY ONLY, and that is a schema gap, not a design. The backend's
   * conversation history persists `{ role, content }` STRINGS, so a graph has
   * nowhere to live across a reload or a resumed conversation — see the fuller
   * note at the re-injection site in `policies/backendPolicy.ts`.
   */
  lastComposedGraph: CandidateGraph | null;
  /**
   * Translation key for the chat input's placeholder, set by `panel/open`
   * when the caller wants the seller nudged toward a specific starting point
   * (e.g. a future "New rule" button — "What do you want to build?").
   *
   * CDSE state, not a component prop — TV1-2385 H-2 explicitly overrides
   * TV1-2340's "component prop, no CDSE state": `AIAssistantPanel` mounts
   * once at `pages/Layout/index.tsx` with `{ className?: string }` and no
   * prop path down to `AIAssistantChat`'s input.
   *
   * Cleared on panel close (`panel/toggle` closing, `panel/confirmOpen` /
   * `panel/cancelOpen` do NOT clear it — they are what SET it) and on the
   * seller's first `message/addUser`, so a stale nudge never survives past
   * the turn it was meant for.
   */
  inputPlaceholderKey: string | null;
  /**
   * Set by `panel/open` when a payload arrives while a conversation is
   * already live — the open is NOT applied until the caller resolves this via
   * `panel/confirmOpen` (proceed, conversation reset) or `panel/cancelOpen`
   * (open on the existing conversation). See `PanelCommands['panel/open']`
   * for why: the conversation-history surface cannot restore a displaced
   * conversation (TV1-2385 H-5).
   */
  pendingPanelOpenConfirmation: { placeholderKey: string } | null;
}

export const initialState: AIAssistantState = {
  isPanelOpen: false,
  panelMode: 'floating',
  conversationId: null,
  messages: [],
  isProcessing: false,
  processingStep: '',
  // Default ON: client plans only navigate + fill forms (+ pause on __human) and
  // always STOP at Save — the Save button is the human approval (TV1-1826 SDD).
  // Backend mutations are rejected from client plans, so auto-executing a plan is
  // safe; the deterministic navigation fast-path relies on it. Users can toggle
  // it off (AutoExecuteToggle) to review each plan behind the Execute button.
  autoExecute: true,
  executingPlanMessageId: null,
  currentExecutingStepIndex: 0,
  conversationHistory: [],
  isLoadingHistory: false,
  processingPhase: null,
  stepError: null,
  planValidationError: null,
  pendingApproval: null,
  pendingApprovalDecision: null,
  lastComposedGraph: null,
  inputPlaceholderKey: null,
  pendingPanelOpenConfirmation: null
};

export const selectors = {
  isPanelOpen: ( state: AIAssistantState ) => state.isPanelOpen,
  panelMode: ( state: AIAssistantState ) => state.panelMode,
  isPanelFullscreen: ( state: AIAssistantState ) => state.panelMode === 'fullscreen',
  messages: ( state: AIAssistantState ) => state.messages,
  isProcessing: ( state: AIAssistantState ) => state.isProcessing,
  processingStep: ( state: AIAssistantState ) => state.processingStep,
  autoExecute: ( state: AIAssistantState ) => state.autoExecute,
  executingPlanMessageId: ( state: AIAssistantState ) => state.executingPlanMessageId,
  currentExecutingStepIndex: ( state: AIAssistantState ) => state.currentExecutingStepIndex,
  conversationHistory: ( state: AIAssistantState ) => state.conversationHistory,
  isLoadingHistory: ( state: AIAssistantState ) => state.isLoadingHistory,
  conversationId: ( state: AIAssistantState ) => state.conversationId,
  processingPhase: ( state: AIAssistantState ) => state.processingPhase,
  stepError: ( state: AIAssistantState ) => state.stepError,
  planValidationError: ( state: AIAssistantState ) => state.planValidationError,
  pendingApproval: ( state: AIAssistantState ) => state.pendingApproval,
  pendingApprovalDecision: ( state: AIAssistantState ) => state.pendingApprovalDecision,
  lastComposedGraph: ( state: AIAssistantState ) => state.lastComposedGraph,
  inputPlaceholderKey: ( state: AIAssistantState ) => state.inputPlaceholderKey,
  pendingPanelOpenConfirmation: ( state: AIAssistantState ) => state.pendingPanelOpenConfirmation
};
